<template>
  <div class="card" style="min-height: calc(100vh - 180px)">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          :showExtraBtn="true"
          :showAddNew="true"
          title="Production Plan List"
          btnTitle="Add New"
          extraBtnTitle="Calendar View"
          @onClickAddNewButton="onOpenAddModal"
          @onClickExtraBtn="navigateToCalendarPage"
      >
        <template #extra-btn>
          <button
              style="min-width: 64px;margin-right: 25px;"
              class="btn btn-primary waves-effect waves-float waves-light"
              @click="onClickPrint"
          >
            <i class="fas fa-print"></i>
            Print
          </button>
        </template>
      </TitleButton>
    </div>

    <div class="px-2">
      <DateQuerySetter btnTitle="Go" @onClickGo="onFilterGo">
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Product/Item</label>
          <AsyncSelect
              placeholder="Select Product"
              v-model="product"
              :api-service="fetchProductDropdownList"
              :format-label="option => option.text"
              label="text"
              :additional-query="{ignore_stock: 1}"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Machine/Line</label>
          <AsyncSelect
              placeholder="Select Machine"
              v-model="machine"
              :api-service="fetchProductDropdownList"
              :format-label="option => option.text"
              label="text"
              :additional-query="{product_type: 'fixed_assets', ignore_stock: 1}"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Search</label>
          <input v-model="searchTerm" type="text" class="form-control" placeholder="Plan no or title"/>
        </div>
      </DateQuerySetter>
    </div>

    <div class="px-2 mt-1">
      <ul class="nav nav-tabs plan-tabs mb-0">
        <li v-for="tab in tabs" :key="tab.name" class="nav-item">
          <a
              :class="['nav-link', { active: statusFilter === tab.value }]"
              @click="setActiveTab(tab)"
          >{{ tab.name }}</a>
        </li>
      </ul>
    </div>

    <div class="col-12 px-2">
      <ListTable :plans="plans" @onView="onViewPlan"/>
    </div>

    <div class="px-2">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <GlobalLoader/>

    <AddOrEditProductionPlanModal
        v-if="store.state.isModalOpen"
        :editData="editData"
        @onSubmit="onPlanSaved"
    />
    <ProductionPlanDetailModal
        v-if="store.state.isModalOpenTwo && singlePlan"
        :plan="singlePlan"
        @onEdit="onEditFromDetail"
        @onChanged="onPlanSaved"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import TitleButton from '@/components/atom/TitleButton'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import Pagination from '@/components/atom/Pagination'
import GlobalLoader from '@/components/atom/GlobalLoader.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import ListTable from '@/components/molecule/company/inventory/production-plan/ListTable.vue'
import AddOrEditProductionPlanModal
  from '@/components/molecule/company/inventory/production-plan/AddOrEditProductionPlanModal.vue'
import ProductionPlanDetailModal
  from '@/components/molecule/company/inventory/production-plan/ProductionPlanDetailModal.vue'
import handlePurchase from '@/services/modules/purchase'
import handleDropdowns from '@/services/modules/dropdowns'
import handleCompany from '@/services/modules/company'
import pdfPrinter from '@/services/utils/pdf/productionPlanPdf'

const route = useRoute()
const router = useRouter()
const store = useStore()

const {
  fetchProductionPlanList,
  fetchProductionPlanStatuses,
  fetchSingleProductionPlan
} = handlePurchase()
const { fetchProductDropdownList } = handleDropdowns()
const { fetchCompanyInfo } = handleCompany()
const { exportToPDF } = pdfPrinter()

const offset = ref(20)
const plans = ref([])
const statusList = ref([])
const statusFilter = ref(null)
const product = ref(null)
const machine = ref(null)
const searchTerm = ref('')
const singlePlan = ref(null)
const editData = ref(null)

const tabs = computed(() => {
  return [{ name: 'All', value: null }].concat(statusList.value)
})

const companyId = computed(() => route.params.companyId)
const startDate = computed(() => route.query.start)
const endDate = computed(() => route.query.end)
const page = computed(() => route.query.page)

function getQuery(isAll = false) {
  let query = '?company_id=' + companyId.value
  if (statusFilter.value) query += '&status=' + statusFilter.value
  if (product.value) query += '&product_id=' + product.value.id
  if (machine.value) query += '&product_machine_id=' + machine.value.id
  if (searchTerm.value) query += '&search_term=' + searchTerm.value
  if (startDate.value) query += '&start_date=' + startDate.value
  if (endDate.value) query += '&end_date=' + endDate.value
  if (isAll) return query + '&is_all=1'
  if (page.value) query += '&page=' + page.value
  query += '&offset=' + offset.value
  return query
}

function getPlanList() {
  return fetchProductionPlanList(getQuery()).then((res) => {
    if (!res.status) {
      plans.value = []
      return store.commit('resetPagination')
    }
    plans.value = res.data.data
    setPagination(res.data)
  })
}

function setPagination(data) {
  if (!data.data.length) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page
  })
}

async function setActiveTab(tab) {
  if (statusFilter.value === tab.value) return
  statusFilter.value = tab.value
  await resetToFirstPage()
  getPlanList()
}

async function onFilterGo() {
  await resetToFirstPage()
  getPlanList()
}

function resetToFirstPage() {
  if (!route.query.page) return Promise.resolve()
  const routeQuery = Object.assign({}, route.query)
  delete routeQuery.page
  return router.push({ path: route.path, query: routeQuery })
}

async function onPageChange(pageNumber) {
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = pageNumber
  await router.push({ path: route.path, query: routeQuery })
  getPlanList()
}

function getStatusList() {
  return fetchProductionPlanStatuses(`?company_id=${companyId.value}`).then((res) => {
    if (!res.status) return
    statusList.value = res.data
  })
}

async function onViewPlan(plan) {
  await fetchSingleProductionPlan(plan.id, `?company_id=${companyId.value}`).then((res) => {
    if (!res.status || !res.data) return
    singlePlan.value = res.data
    store.state.isModalOpenTwo = true
  }).catch(() => {
    singlePlan.value = null
  })
}

function onOpenAddModal() {
  editData.value = null
  store.state.isModalOpen = true
}

function onEditFromDetail(plan) {
  editData.value = plan
  store.state.isModalOpenTwo = false
  store.state.isModalOpen = true
}

function onPlanSaved() {
  getPlanList()
}

function getPrintPayload() {
  const payload = {
    status: null,
    productName: null,
    machineName: null,
    searchTerm: searchTerm.value
  }

  if (statusFilter.value) payload.status = statusLabel(statusFilter.value)
  if (product.value) payload.productName = product.value.text
  if (machine.value) payload.machineName = machine.value.text

  return payload
}

function statusLabel(value) {
  const status = statusList.value.find((item) => item.value === value)
  if (!status) return value
  return status.name
}

async function onClickPrint() {
  store.state.loading = true

  const printData = await fetchProductionPlanList(getQuery(true)).then((res) => {
    if (!res.status) return []
    return res.data
  }).catch(() => {
    return []
  })

  if (!printData.length) {
    store.state.loading = false
    return
  }

  await fetchCompanyInfo(companyId.value).then((res) => {
    if (!res.data) return
    const date = { startDate: startDate.value, endDate: endDate.value }
    exportToPDF(res.data, printData, date, getPrintPayload())
  }).finally(() => {
    store.state.loading = false
  })
}

function navigateToCalendarPage() {
  const query = Object.assign({}, route.query)

  if (!isAnchorInsideRange(query.anchor)) {
    query.anchor = startDate.value
  }

  router.push({
    name: 'production-plan-calendar',
    params: route.params,
    query: query
  })
}

function isAnchorInsideRange(anchor) {
  if (!anchor) return false
  if (!startDate.value || !endDate.value) return false
  return anchor >= startDate.value && anchor <= endDate.value
}

onMounted(() => {
  store.state.isModalOpen = false
  store.state.isModalOpenTwo = false
  getStatusList()
  getPlanList()
})
</script>

<style scoped>
.plan-tabs {
  border-bottom: 1px solid #eef0f4;
  gap: 0.15rem;
  flex-wrap: wrap;
}

.plan-tabs .nav-link {
  cursor: pointer;
  border: 0;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  padding: 0.45rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
}

.plan-tabs .nav-link:hover {
  color: #4b5563;
  background: #f9fafb;
}

.plan-tabs .nav-link.active {
  color: #6366f1;
  background: transparent;
  border-bottom-color: #6366f1;
}
</style>
